import * as React from 'react';
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '../primitives/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../primitives/tooltip';
import { ScrollArea } from '../primitives/scroll-area';
import { cn } from '../lib/utils';

export interface MenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  href?: string;
  onClick?: () => void;
  children?: MenuItem[];
  badge?: React.ReactNode;
  /** Optional ISA-95 group (Operate / Plan / Commerce / People / Admin). */
  group?: string;
  /** Optional pin flag — pinned items appear in their own section above groups. */
  pinned?: boolean;
}

export interface SidebarSection {
  id: string;
  label: string;
  items: MenuItem[];
}

export interface SidebarProps {
  menuItems: MenuItem[];
  /** Optional pre-computed sections; if provided, supersedes menuItems grouping. */
  sections?: SidebarSection[];
  activeMenuId?: string;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onMenuClick?: (item: MenuItem) => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /**
   * Optional slot rendered between the header and the nav scroll area (e.g. a
   * nav filter box). Hidden in the collapsed mini-icon rail.
   */
  subheader?: React.ReactNode;
  /**
   * Controlled per-section collapse. When `onSectionToggle` is provided each
   * section label becomes a disclosure button; sections whose id is listed in
   * `collapsedSectionIds` hide their items. Only applies to the expanded rail —
   * the mini-icon rail keeps every item reachable via tooltips. Omitting the
   * handler preserves the previous always-expanded behaviour.
   */
  collapsedSectionIds?: readonly string[];
  onSectionToggle?: (sectionId: string) => void;
  /**
   * When true, a menu item's children start collapsed unless the item is on
   * the active trail (and auto-expand when it becomes so). Default false — the
   * historical always-expanded behaviour for existing consumers.
   */
  autoCollapseChildren?: boolean;
  /**
   * Force-expand every item's children and every section (e.g. while a nav
   * filter query is active, so matches are never hidden by collapse state).
   */
  expandAll?: boolean;
}

/**
 * Workspace-grade sidebar. Supports flat menus, ISA-95 grouped sections, and
 * collapsed mini-icon mode with tooltips. Token-driven (surface/border/primary).
 */
export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      menuItems,
      sections,
      activeMenuId,
      collapsed = false,
      onCollapsedChange,
      onMenuClick,
      header,
      footer,
      className,
      subheader,
      collapsedSectionIds,
      onSectionToggle,
      autoCollapseChildren = false,
      expandAll = false,
    },
    ref
  ) => {
    return (
      <TooltipProvider delayDuration={0}>
        <aside
          ref={ref}
          className={cn(
            // macOS-style inset vibrancy rail — glass-strong fill, floating
            // rounded panel with a hairline edge. motion-reduce disables the
            // width-slide transition for users who opt into reduced motion
            // (WCAG 2.3.3). Spring easing gives the collapse a gentle settle.
            'glass-strong m-2 flex h-[calc(100%-1rem)] flex-col overflow-hidden rounded-2xl border border-border/60 text-foreground shadow-[var(--shadow-md)] transition-all duration-200 [transition-timing-function:var(--ease-spring)] motion-reduce:transition-none motion-reduce:transform-none',
            collapsed ? 'w-[60px]' : 'w-[240px]',
            className
          )}
        >
          {header && (
            <div
              className={cn(
                'flex h-14 items-center border-b border-border/60 px-3 shrink-0',
                collapsed && 'justify-center px-0'
              )}
            >
              {header}
            </div>
          )}

          {subheader && !collapsed && (
            <div className="border-b border-border/60 px-2 py-2 shrink-0">
              {subheader}
            </div>
          )}

          <ScrollArea className="flex-1 py-2">
            <nav className="space-y-1 px-2" aria-label="Primary">
              {/* A PROVIDED `sections` array always wins — including when it is
                  empty. The previous `sections.length > 0` test silently fell
                  back to the raw `menuItems` whenever the caller's sections
                  reduced to none, which is exactly when the caller had narrowed
                  them: the shell passes entitlement-gated sections and the
                  ungated flat list, so filtering the nav to a term that matched
                  no PERMITTED module re-rendered the whole catalogue —
                  unentitled modules included. An empty provided array means
                  "nothing to show", never "show everything". */}
              {sections
                ? sections.map((section) => (
                    <SidebarSectionView
                      key={section.id}
                      section={section}
                      activeMenuId={activeMenuId}
                      collapsed={collapsed}
                      onMenuClick={onMenuClick}
                      sectionCollapsed={
                        !expandAll &&
                        (collapsedSectionIds?.includes(section.id) ?? false)
                      }
                      onSectionToggle={onSectionToggle}
                      autoCollapseChildren={autoCollapseChildren}
                      expandAll={expandAll}
                    />
                  ))
                : menuItems.map((item) => (
                    <MenuItemView
                      key={item.id}
                      item={item}
                      activeMenuId={activeMenuId}
                      collapsed={collapsed}
                      onMenuClick={onMenuClick}
                      autoCollapseChildren={autoCollapseChildren}
                      expandAll={expandAll}
                    />
                  ))}
            </nav>
          </ScrollArea>

          <div className="border-t border-border/60 p-2 shrink-0">
            {footer && !collapsed && <div className="mb-2">{footer}</div>}
            {onCollapsedChange && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="w-full justify-center text-muted-foreground hover:text-foreground"
                onClick={() => onCollapsedChange(!collapsed)}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <ChevronLeft
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    collapsed && 'rotate-180'
                  )}
                />
              </Button>
            )}
          </div>
        </aside>
      </TooltipProvider>
    );
  }
);

Sidebar.displayName = 'Sidebar';

interface SidebarSectionViewProps {
  section: SidebarSection;
  activeMenuId?: string;
  collapsed: boolean;
  onMenuClick?: (item: MenuItem) => void;
  /** Controlled collapse state for this section (expanded rail only). */
  sectionCollapsed?: boolean;
  onSectionToggle?: (sectionId: string) => void;
  autoCollapseChildren?: boolean;
  expandAll?: boolean;
}

function SidebarSectionView({
  section,
  activeMenuId,
  collapsed,
  onMenuClick,
  sectionCollapsed = false,
  onSectionToggle,
  autoCollapseChildren,
  expandAll,
}: SidebarSectionViewProps) {
  // Stable id so the disclosure button can point aria-controls at the item
  // group (WCAG 4.1.2 — announce expanded/collapsed state + what it controls).
  const reactId = React.useId();
  const groupId = `${reactId}-section-items`;
  if (section.items.length === 0) return null;

  const collapsible = Boolean(onSectionToggle);
  // Section collapse only applies to the expanded rail; the mini-icon rail
  // keeps every item reachable (tooltips carry the labels).
  const itemsHidden = collapsible && sectionCollapsed && !collapsed;

  return (
    <div className={cn('space-y-0.5', !collapsed && 'pb-3')}>
      {!collapsed &&
        (collapsible ? (
          <button
            type="button"
            onClick={() => onSectionToggle!(section.id)}
            aria-expanded={!sectionCollapsed}
            aria-controls={itemsHidden ? undefined : groupId}
            className="flex w-full items-center gap-1 rounded-md px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex-1 text-left">{section.label}</span>
            {sectionCollapsed ? (
              <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
            )}
          </button>
        ) : (
          <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {section.label}
          </div>
        ))}
      {collapsed && section.items.length > 0 && (
        <div className="my-2 mx-3 h-px bg-border/60" />
      )}
      {!itemsHidden && (
        <div id={groupId} className="space-y-0.5">
          {section.items.map((item) => (
            <MenuItemView
              key={item.id}
              item={item}
              activeMenuId={activeMenuId}
              collapsed={collapsed}
              onMenuClick={onMenuClick}
              autoCollapseChildren={autoCollapseChildren}
              expandAll={expandAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MenuItemViewProps {
  item: MenuItem;
  activeMenuId?: string;
  collapsed: boolean;
  onMenuClick?: (item: MenuItem) => void;
  depth?: number;
  autoCollapseChildren?: boolean;
  expandAll?: boolean;
}

function MenuItemView({
  item,
  activeMenuId,
  collapsed,
  onMenuClick,
  depth = 0,
  autoCollapseChildren = false,
  expandAll = false,
}: MenuItemViewProps) {
  const hasChildren = Boolean(item.children && item.children.length > 0);
  // Stable id for the child disclosure group so the toggle button can point
  // `aria-controls` at it (WCAG 4.1.2 — a screen-reader announces the
  // collapsed/expanded state and what it controls).
  const reactId = React.useId();
  const childGroupId = hasChildren ? `${reactId}-submenu` : undefined;
  const isActive = activeMenuId === item.id;
  const isParentOfActive =
    hasChildren &&
    item.children!.some(
      (child) =>
        child.id === activeMenuId ||
        child.children?.some((gc) => gc.id === activeMenuId)
    );
  // Historical default: children always start expanded. With
  // `autoCollapseChildren` only the active trail starts expanded so a large
  // module catalogue doesn't render as one long flat scroll.
  const [open, setOpen] = React.useState(
    autoCollapseChildren ? isActive || isParentOfActive : true
  );
  // Auto-reveal the trail when this item becomes the active view's parent
  // (e.g. navigation from the command palette or a deep link).
  React.useEffect(() => {
    if (isParentOfActive || isActive) setOpen(true);
  }, [isParentOfActive, isActive]);
  const effectiveOpen = expandAll || open;
  const Icon = item.icon;

  const handleClick = () => {
    if (hasChildren) {
      setOpen((v) => !v);
    }
    item.onClick?.();
    onMenuClick?.(item);
  };

  const isTopLevel = depth === 0;

  const content = (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'group/item relative flex w-full items-center gap-2.5 rounded-xl text-sm transition-colors [transition-timing-function:var(--ease-spring)] motion-reduce:transition-none',
        isTopLevel ? 'px-2.5 py-2 font-medium' : 'px-2.5 py-1.5',
        'hover:bg-accent/60',
        isActive && 'bg-primary/12 text-primary font-medium',
        !isActive &&
          isParentOfActive &&
          'text-foreground',
        !isActive &&
          !isParentOfActive &&
          'text-muted-foreground hover:text-foreground',
        collapsed && 'justify-center px-0 py-2.5',
        depth > 0 && !collapsed && 'ml-4 pl-3 border-l border-border/60'
      )}
      aria-current={isActive ? 'page' : undefined}
      // Disclosure semantics only apply in the expanded rail, where the
      // chevron shows and the child group is rendered (`open && !collapsed`).
      // In mini-icon mode the children are unreachable, so we omit both
      // attributes rather than point aria-controls at an absent node.
      aria-expanded={hasChildren && !collapsed ? effectiveOpen : undefined}
      aria-controls={hasChildren && !collapsed && effectiveOpen ? childGroupId : undefined}
    >
      {/* Active marker — vertical accent stripe on left */}
      {isActive && !collapsed && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-r bg-primary"
        />
      )}
      {Icon && (
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            isActive
              ? 'text-primary'
              : 'text-muted-foreground group-hover/item:text-foreground'
          )}
        />
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left">{item.label}</span>
          {item.badge}
          {hasChildren && (
            <span className="shrink-0">
              {effectiveOpen ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </span>
          )}
        </>
      )}
    </button>
  );

  return (
    <div>
      {collapsed && Icon ? (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      ) : (
        content
      )}
      {hasChildren && effectiveOpen && !collapsed && (
        <div id={childGroupId} className="mt-0.5 space-y-0.5">
          {item.children!.map((child) => (
            <MenuItemView
              key={child.id}
              item={child}
              activeMenuId={activeMenuId}
              collapsed={collapsed}
              onMenuClick={onMenuClick}
              depth={depth + 1}
              autoCollapseChildren={autoCollapseChildren}
              expandAll={expandAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}
